import { CryptoCurrencyId, CryptoCurrencyMeta, CryptoCurrencyStaticMeta, CryptoCurrencyStatistics } from 'components/features/rewards/rewardsPane/types/cryptoCurrency'
import { State } from 'interfaces/state'
import { getPaneReducerStateSelector } from 'components/features/rewards/rewardsPane/store/selectors/generalReducer'
import { createSelector } from 'reselect'
import { CRYPTO_REWARDS_PANE_DEFAULTS_CURRENCY_STATISTICS } from 'components/features/rewards/rewardsPane/constants/defaults'
import { CurrenciesMetaReducer, CurrenciesMetaReducerCurrencyItem, CurrenciesStatisticsReducer } from 'components/features/rewards/rewardsPane/types/store'
import { Collection } from 'interfaces'

export function getCryptoCurrenciesStaticMetaReducerStateSelector (storeState: State): CurrenciesMetaReducer {
  return getPaneReducerStateSelector(storeState).currenciesMeta
}

export function cryptoCurrenciesMetaSelector (storeState: State): Collection<CurrenciesMetaReducerCurrencyItem, 'id'> | null {
  return getCryptoCurrenciesStaticMetaReducerStateSelector(storeState).currenciesMeta ?? null
}

export const getCryptoCurrenciesDefaultCurrencyIdOrNullSelector = createSelector(
  cryptoCurrenciesMetaSelector,
  (currenciesMetaOrNull: Collection<CurrenciesMetaReducerCurrencyItem, 'id'> | null): CryptoCurrencyId | null => {
    const currencyIdMarkedAsDefault = currenciesMetaOrNull?.allKeys.find((currencyId: CryptoCurrencyId) => Boolean(currenciesMetaOrNull.byKey[currencyId]?.isDefault)) ?? null
    return currencyIdMarkedAsDefault
  }
)

export const getCryptoCurrenciesAllowedToBuyStaticMetaSelector = createSelector(
  cryptoCurrenciesMetaSelector,
  (cryptoCurrenciesAllowedToBuyOrNull: Collection<CurrenciesMetaReducerCurrencyItem, 'id'> | null): Record<CryptoCurrencyId, CryptoCurrencyStaticMeta> => (
    cryptoCurrenciesAllowedToBuyOrNull
      ? cryptoCurrenciesAllowedToBuyOrNull.allKeys.reduce<Record<CryptoCurrencyId, CryptoCurrencyStaticMeta>>((
        (acc, cryptoCurrencyId) => {
          const cryptoCurrencyMetaOrNull = cryptoCurrenciesAllowedToBuyOrNull.byKey[cryptoCurrencyId] ?? null
          if (cryptoCurrencyMetaOrNull?.isAllowedToBuy) {
            acc[cryptoCurrencyId] = cryptoCurrencyMetaOrNull
          }
          return acc
        }
      ), {})
      : {}
  )
)

function getCryptoCurrenciesStatisticsReducerStateSelector (storeState: State): CurrenciesStatisticsReducer {
  return getPaneReducerStateSelector(storeState).currenciesStatistics
}

export function cryptoCurrenciesMarketSelector (storeState: State): Collection<CryptoCurrencyStatistics, 'id'> | null {
  return getCryptoCurrenciesStatisticsReducerStateSelector(storeState).currenciesStatistics ?? null
}

export const getCryptoCurrenciesMetaDataWithMarketDataSelector = createSelector(
  getCryptoCurrenciesAllowedToBuyStaticMetaSelector,
  cryptoCurrenciesMarketSelector,
  (
    currenciesStaticMeta: Record<CryptoCurrencyId, CryptoCurrencyStaticMeta>,
    currenciesStatistics: Collection<CryptoCurrencyStatistics, 'id'> | null,
  ): CryptoCurrencyMeta[] => (
    Object.keys(currenciesStaticMeta).reduce((currenciesMetaAcc: CryptoCurrencyMeta[], currencyId: CryptoCurrencyId): CryptoCurrencyMeta[] => {
      const currencyStatistics = currenciesStatistics?.byKey[currencyId]
      if (!currencyStatistics) {
        return currenciesMetaAcc
      }
      const currencyStaticMeta = currenciesStaticMeta[currencyId]
      const currencyMeta = {
        ...CRYPTO_REWARDS_PANE_DEFAULTS_CURRENCY_STATISTICS,
        ...currencyStatistics,
        ...currencyStaticMeta,
      }

      currenciesMetaAcc.push(currencyMeta)
      return currenciesMetaAcc
    }, [] as CryptoCurrencyMeta[])
  )
)

export function isCryptocurrenciesStaticMetaLoadingSelector (storeState: State): boolean {
  return Boolean(getCryptoCurrenciesStaticMetaReducerStateSelector(storeState).isLoading)
}

export function isCryptocurrenciesStatisticsLoadingSelector (storeState: State): boolean {
  return Boolean(getCryptoCurrenciesStatisticsReducerStateSelector(storeState).isLoading)
}

export function isCryptocurrenciesMetaListLoadingSelector (storeState: State): boolean {
  return isCryptocurrenciesStatisticsLoadingSelector(storeState) || isCryptocurrenciesStaticMetaLoadingSelector(storeState)
}

export const getCryptoCurrencyMetaOrNullById = createSelector(
  cryptoCurrenciesMetaSelector,
  (_store: State, currencyId: CryptoCurrencyId) => currencyId,
  (currenciesMetaOrNull: Collection<CurrenciesMetaReducerCurrencyItem, 'id'> | null, currencyId): CurrenciesMetaReducerCurrencyItem | null => (
    currenciesMetaOrNull?.byKey[currencyId] ?? null
  )
)
